/**
 * HumiditySensorModalClassic — Monolithic humidity sensor modal template.
 *
 * Read-only humidity sensor display panel:
 * - Humidity: large % display with water drop icon
 * - Comfort: comfort level indicator (Dry/Comfortable/Humid based on % thresholds)
 *
 * No commands — purely observational.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './HumiditySensorModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const HUMIDITY_SENSOR_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Humidity Section ──
  { id: 'humidity', label: 'Humidity Reading', icon: 'water_drop', slots: [
    { id: 'humidity-label', label: 'Title "HUMIDITY"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'humidity-icon', label: 'Water Drop Icon', editableProps: ['fontSize', 'color'] },
    { id: 'humidity-value', label: 'Humidity Number (large)', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'humidity-unit', label: 'Unit "%"', editableProps: ['fontSize', 'color'] },
  ]},
  // ── Comfort Section ──
  { id: 'comfort', label: 'Comfort Level', icon: 'sentiment_satisfied', slots: [
    { id: 'comfort-label', label: 'Title "COMFORT LEVEL"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'comfort-badge', label: 'Comfort Badge', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize', 'fontWeight'],
      states: [{ id: 'dry', label: 'Dry' }, { id: 'comfortable', label: 'Comfortable' }, { id: 'humid', label: 'Humid' }] },
    { id: 'comfort-bar-fill', label: 'Comfort Bar Fill', editableProps: ['background', 'height', 'borderRadius'] },
    { id: 'comfort-desc', label: 'Comfort Description Text', editableProps: ['fontSize', 'color'] },
    { id: 'comfort-range', label: 'Optimal Range Text', editableProps: ['fontSize', 'color'] },
  ]},
]

const DEFAULT_SECTION_ORDER = HUMIDITY_SENSOR_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Comfort level thresholds
// ---------------------------------------------------------------------------

type ComfortLevel = { id: 'dry' | 'comfortable' | 'humid'; label: string; desc: string; color: string; percent: number }

function getComfortLevel(humidity: number): ComfortLevel {
  if (humidity < 30) return {
    id: 'dry',
    label: 'Dry',
    desc: 'Air is too dry — may cause irritation',
    color: '#f97316',
    percent: Math.max(5, (humidity / 30) * 33),
  }
  if (humidity <= 60) return {
    id: 'comfortable',
    label: 'Comfortable',
    desc: 'Ideal humidity range for comfort',
    color: '#3b82f6',
    percent: 33 + ((humidity - 30) / 30) * 34,
  }
  return {
    id: 'humid',
    label: 'Humid',
    desc: 'Air is too humid — may feel muggy',
    color: '#8b5cf6',
    percent: Math.min(100, 67 + ((humidity - 60) / 40) * 33),
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HumiditySensorModalClassic({
  device,
  deviceState,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const rawHumidity = numAttr(deviceState, 'humidityAmbientPercent', 'humidity', 'relativeHumidity')
  const displayHumidity = rawHumidity ?? (isEditorMode ? 55 : null)

  const comfort = useMemo(() => displayHumidity !== null ? getComfortLevel(displayHumidity) : null, [displayHumidity])

  return (
    <div
      className={`humidity-sensor-modal-classic${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="humidity-sensor-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="humidity-sensor-modal-classic__header">
        <button className="humidity-sensor-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="humidity-sensor-modal-classic__title" data-modal-slot="header-title">{device.name || 'Humidity Sensor'}</h2>
        <button
          className="humidity-sensor-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'humidity':
            return (
              <div key="humidity" className={`humidity-sensor-modal-classic__humidity${hl}`} data-modal-section="humidity">
                <div className="humidity-sensor-modal-classic__section-label" data-modal-slot="humidity-label">HUMIDITY</div>
                <div className="humidity-sensor-modal-classic__humidity-display">
                  <span
                    className="material-symbols-rounded humidity-sensor-modal-classic__humidity-icon"
                    data-modal-slot="humidity-icon"
                    style={comfort ? { color: comfort.color } : undefined}
                  >
                    water_drop
                  </span>
                  <div className="humidity-sensor-modal-classic__humidity-number">
                    <span
                      className="humidity-sensor-modal-classic__humidity-value"
                      data-modal-slot="humidity-value"
                      style={comfort ? { color: comfort.color } : undefined}
                    >
                      {displayHumidity !== null ? displayHumidity.toFixed(0) : '—'}
                    </span>
                    <span className="humidity-sensor-modal-classic__humidity-unit" data-modal-slot="humidity-unit">%</span>
                  </div>
                </div>
              </div>
            )
          case 'comfort':
            return (
              <div key="comfort" className={`humidity-sensor-modal-classic__comfort${hl}`} data-modal-section="comfort">
                <div className="humidity-sensor-modal-classic__section-label" data-modal-slot="comfort-label">COMFORT LEVEL</div>
                {comfort ? (
                  <>
                    <div className="humidity-sensor-modal-classic__comfort-row">
                      <span
                        className="humidity-sensor-modal-classic__comfort-badge"
                        data-modal-slot="comfort-badge"
                        data-modal-slot-state={comfort.id}
                        style={{ color: comfort.color, borderColor: comfort.color + '44' }}
                      >
                        {comfort.label}
                      </span>
                      <span className="humidity-sensor-modal-classic__comfort-desc" data-modal-slot="comfort-desc">
                        {comfort.desc}
                      </span>
                    </div>
                    <div className="humidity-sensor-modal-classic__comfort-bar-track">
                      <div
                        className="humidity-sensor-modal-classic__comfort-bar-fill"
                        data-modal-slot="comfort-bar-fill"
                        style={{ width: `${comfort.percent}%`, background: comfort.color }}
                      />
                    </div>
                    <span className="humidity-sensor-modal-classic__comfort-range" data-modal-slot="comfort-range">
                      Optimal range: 30 – 60%
                    </span>
                  </>
                ) : (
                  <div className="humidity-sensor-modal-classic__empty">No humidity data available</div>
                )}
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
